<template>
  <section class="panel panel-default drives-heat-map">
    <div class="panel-heading">
      <strong>Completed Drives by Pickup ZIP</strong>
    </div>
    <div class="panel-body">
      <l-map
        v-if="hasLayers"
        :style="`height: 500px; width: 100%`"
        :center="[0, 0]"
        :zoom="11"
        :bounds="bounds"
        :options="leafletOptions"
        ref="heatMap"
      >
        <l-tile-layer v-bind="basemap" />

        <l-geo-json
          v-for="(layer, index) in processedLayers"
          :key="index"
          :geojson="layer.geojson"
          :options="geoJSONOptions"
          :optionsStyle="layer.style"
        >
        </l-geo-json>

        <l-control position="bottomright">
          <div class="map-info map-legend">
            <strong>Completed drives</strong>
            <div class="legend-scale">
              <i
                v-for="(color, index) in palette"
                :key="index"
                class="legend-swatch"
                :style="'background-color: ' + color"
              ></i>
            </div>
            <div class="legend-range">
              <span>1</span>
              <span>{{ maxSessions }}</span>
            </div>
          </div>
        </l-control>
      </l-map>

      <div v-else class="alert alert-info inline text-center">
        No mapped pickup ZIPs for this period.
      </div>

      <p v-if="unknownSessions > 0" class="text-muted" style="margin-top: 8px">
        {{ unknownSessions }} completed drive(s) have no pickup location on file
        and are not shown on the map.
      </p>

      <table
        v-if="rankedZips.length"
        class="table table-condensed"
        style="margin-top: 12px"
      >
        <thead>
          <tr>
            <th>ZIP</th>
            <th class="text-right">Drives</th>
            <th class="text-right">% of total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rankedZips" :key="row.zip_code">
            <td>{{ row.zip_code }}</td>
            <td class="text-right">{{ row.sessions }}</td>
            <td class="text-right">{{ row.percentage_of_total }}%</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import { geoJSON } from "leaflet";
import { LMap, LControl, LGeoJson, LTileLayer } from "vue2-leaflet";
import { BASEMAP } from "../../../../DriveScout/Config.js";

// Sequential YlOrRd ramp: lighter = fewer drives, darker = more.
const PALETTE = ["#ffffb2", "#fecc5c", "#fd8d3c", "#f03b20", "#bd0026"];
const UNKNOWN_LABEL = "Unknown";

export default {
  name: "DrivesHeatMap",
  components: { LMap, LControl, LGeoJson, LTileLayer },
  props: {
    locationBreakdown: { type: Object, required: true },
    zipBoundaries: {
      type: Array,
      default: function () {
        return [];
      },
    },
  },
  data: function () {
    return {
      basemap: BASEMAP,
      palette: PALETTE,
      leafletOptions: { scrollWheelZoom: false },
      geoJSONOptions: {
        onEachFeature: function (feature, layer) {
          if (feature.properties && feature.properties.tooltip) {
            layer.bindTooltip(feature.properties.tooltip, {
              sticky: true,
              className: "drives-heat-map-tooltip",
            });
          }
        },
      },
    };
  },
  computed: {
    byZip: function () {
      return (
        (this.locationBreakdown && this.locationBreakdown.by_zip_code) || []
      );
    },

    rankedZips: function () {
      return this.byZip.filter(function (row) {
        return row.zip_code !== UNKNOWN_LABEL;
      });
    },

    unknownSessions: function () {
      var unknown = this.byZip.find(function (row) {
        return row.zip_code === UNKNOWN_LABEL;
      });
      return unknown ? unknown.sessions : 0;
    },

    sessionsByZip: function () {
      var counts = {};
      this.rankedZips.forEach(function (row) {
        counts[String(row.zip_code)] = row;
      });
      return counts;
    },

    maxSessions: function () {
      return this.rankedZips.reduce(function (max, row) {
        return Math.max(max, row.sessions);
      }, 0);
    },

    processedLayers: function () {
      var self = this;

      return (this.zipBoundaries || [])
        .map(function (boundary) {
          var feature = self.polygonFeature(boundary.geojson);
          if (!feature) {
            return null;
          }

          var zip = String(boundary.zip);
          var row = self.sessionsByZip[zip];
          if (!row) {
            return null;
          }

          var geojson = {
            type: "Feature",
            geometry: feature.geometry,
            properties: {
              tooltip:
                "ZIP " +
                zip +
                ": " +
                row.sessions +
                " drive(s) (" +
                row.percentage_of_total +
                "%)",
            },
          };

          return {
            geojson: geojson,
            style: self.styleForSessions(row.sessions),
          };
        })
        .filter(function (layer) {
          return layer !== null;
        });
    },

    hasLayers: function () {
      return this.processedLayers.length > 0;
    },

    bounds: function () {
      if (!this.processedLayers.length) {
        return null;
      }
      try {
        return geoJSON({
          type: "FeatureCollection",
          features: this.processedLayers.map(function (layer) {
            return layer.geojson;
          }),
        }).getBounds();
      } catch (error) {
        return null;
      }
    },
  },
  methods: {
    // Accepts a bare Polygon/MultiPolygon geometry (zonepart geom), a Feature,
    // or a FeatureCollection, and returns a Feature with a polygon geometry.
    polygonFeature: function (geojson) {
      if (!geojson || !geojson.type) {
        return null;
      }

      if (geojson.type === "Polygon" || geojson.type === "MultiPolygon") {
        return { type: "Feature", geometry: geojson, properties: {} };
      }

      if (geojson.type === "Feature") {
        return this.isPolygon(geojson.geometry) ? geojson : null;
      }

      if (geojson.type === "FeatureCollection" && geojson.features) {
        var self = this;
        return (
          geojson.features.find(function (feature) {
            return self.isPolygon(feature.geometry);
          }) || null
        );
      }

      return null;
    },

    isPolygon: function (geometry) {
      return (
        geometry &&
        (geometry.type === "Polygon" || geometry.type === "MultiPolygon")
      );
    },

    colorForSessions: function (sessions) {
      if (this.maxSessions <= 0) {
        return PALETTE[0];
      }
      var index = Math.min(
        PALETTE.length - 1,
        Math.floor((sessions / this.maxSessions) * PALETTE.length),
      );
      return PALETTE[index];
    },

    styleForSessions: function (sessions) {
      return {
        fillColor: this.colorForSessions(sessions),
        weight: 1,
        opacity: 1,
        color: "#666666",
        fillOpacity: 0.7,
      };
    },
  },
  beforeDestroy: function () {
    if (this.$refs.heatMap && this.$refs.heatMap.mapObject) {
      this.$refs.heatMap.mapObject.off();
    }
  },
};
</script>

<style scoped>
.map-info {
  padding: 8px 10px;
  font:
    14px/18px Arial,
    Helvetica,
    sans-serif;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
  border-radius: 5px;
}
.map-legend strong {
  display: block;
  margin-bottom: 6px;
}
.legend-scale {
  display: flex;
}
.legend-swatch {
  width: 24px;
  height: 14px;
  display: inline-block;
}
.legend-range {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin-top: 2px;
}
</style>

<style>
.drives-heat-map-tooltip {
  font-size: 14px;
  line-height: 1.4;
  padding: 8px 12px;
}
</style>
